/**
 * Admin 공통 폼: Form.ts 활용 (async, await, try/catch, submitting, disabled, loader)
 * data-admin-ajax="1" 인 폼에 자동 바인딩. 제출 시 fetch + JSON 응답 처리, redirect 또는 메시지.
 * admin-auth__form 은 AdminAuthController에서 처리하므로 제외.
 */

import { Form } from "../../core/Form";
import { Modal } from "../../utils/Modal";

const SELECTOR = 'form[data-admin-ajax="1"]';
const LOADER_SELECTOR = ".admin-form__loader";

function getRedirect(data: Record<string, unknown>): string | undefined {
  const r = data?.redirect ?? (data?.data as Record<string, unknown> | undefined)?.redirect;
  return typeof r === "string" ? r : undefined;
}

function getMessage(data: Record<string, unknown>): string {
  return (data?.msg ?? data?.message ?? "") as string;
}

export function initAdminForms(): void {
  const forms = document.querySelectorAll<HTMLFormElement>(SELECTOR);
  forms.forEach((form) => {
    if (form.classList.contains("admin-auth__form")) return;
    const url = form.action || form.dataset.submitUrl || "";
    if (!url) return;

    const confirmMsg = form.dataset.confirm;
    const f = new Form(form, {
      submitUrl: url,
      loaderSelector: LOADER_SELECTOR,
      validate:
        confirmMsg != null
          ? (frm) => Modal.confirm(confirmMsg).then((ok) => (ok ? null : "취소되었습니다."))
          : undefined,
      requestFailedMessage: "요청에 실패했습니다.",
      invalidResponseMessage: "응답을 처리할 수 없습니다.",
      onSuccess(data) {
        const redirect = getRedirect(data);
        const msg = getMessage(data);
        if (redirect) {
          window.location.href = redirect;
          return;
        }
        if (msg) void Modal.alert(msg);
      },
      onFail(msg) {
        void Modal.alert(msg);
      },
    });
    f.init();
  });
}

/**
 * admin 영역의 date, datetime-local, time 입력: 인풋 클릭 시 선택기 열기 (showPicker).
 * 브라우저 기본은 우측 아이콘만 클릭 시 열리는 경우가 있어, 인풋 클릭 시 showPicker() 호출로 통일.
 */
export function initAdminDateTimeInputs(): void {
  const root = document.body.classList.contains("admin") ? document.body : document.querySelector(".admin");
  if (!root) return;

  const inputs = root.querySelectorAll<HTMLInputElement>(
    'input[type="date"], input[type="datetime-local"], input[type="time"]',
  );
  inputs.forEach((input) => {
    input.addEventListener("click", () => {
      if (typeof input.showPicker === "function") {
        try {
          input.showPicker();
        } catch {
          // 일부 브라우저/상황에서는 showPicker 미지원
        }
      }
    });
  });
}

import { SelectWrapper } from "../../components/SelectWrapper";

/**
 * admin 영역의 모든 select에 SlimSelect 적용 (SelectWrapper 사용).
 * body.admin 내 select.admin-select, select.admin-form__input, select.admin-auth__input 대상.
 */
export function initAdminSelects(): void {
  if (!document.body.classList.contains("admin")) return;

  const selects = document.querySelectorAll<HTMLSelectElement>(
    "body.admin select.admin-select, body.admin select.admin-form__input, body.admin select.admin-auth__input",
  );
  selects.forEach((el) => {
    const selectedIdsJson = el.getAttribute("data-selected-ids");
    const wrapper = new SelectWrapper(el, { searchPlaceholder: "검색..." });
    const instance = wrapper.init();
    if (selectedIdsJson != null && selectedIdsJson !== "" && instance) {
      try {
        const ids = JSON.parse(selectedIdsJson) as number[];
        const setSelected = (instance as { setSelected?: (v: string[], fire?: boolean) => void }).setSelected;
        if (typeof setSelected === "function") {
          setSelected(ids.map(String), false);
        }
      } catch {
        // ignore invalid JSON
      }
    }
  });
}
